import type { UserAccessEntry } from "@/features/identity-hub/api/identity-hub.api";
import type { UserAccessPhase } from "@/features/identity-hub/lib/merge-users-access";
import { isSuperTenantMember } from "@/features/identity-hub/utils/iam.utils";

/** Each selected user lands in exactly one bucket, so the summary adds up. */
export interface RemoveAllPartition {
  eligible: string[];
  skippedSelf: number;
  loading: number;
  failed: number;
  skippedStaff: number;
  skippedRoleless: number;
}

/**
 * Who a bulk "remove all roles" would touch. Staff and roleless can only be
 * judged once a user's roles have loaded, so loading and failed users are
 * counted on their own instead of being mislabelled as roleless.
 */
export function partitionRemoveAll(
  selectedUserIds: string[],
  accessByUser: Map<string, UserAccessEntry[]>,
  accessPhaseByUser: Map<string, UserAccessPhase>,
  loggedInUserId: string | null,
): RemoveAllPartition {
  const result: RemoveAllPartition = {
    eligible: [], skippedSelf: 0, loading: 0, failed: 0, skippedStaff: 0, skippedRoleless: 0,
  };
  for (const userId of selectedUserIds) {
    const entries = accessByUser.get(userId);
    const phase = accessPhaseByUser.get(userId) ?? "loading";
    if (userId === loggedInUserId) result.skippedSelf++;
    else if (phase === "loading") result.loading++;
    else if (phase === "error") result.failed++;
    else if (isSuperTenantMember(entries)) result.skippedStaff++;
    else if (!entries || entries.length === 0) result.skippedRoleless++;
    else result.eligible.push(userId);
  }
  return result;
}
